// --------------------------------------------------------
// OCCUPATIONAL HEALTH REGISTER - OccupationalHealthRegister
// --------------------------------------------------------

// --------------------------------------------------------
// IMPORTS
// --------------------------------------------------------
import { Form, Formik } from 'formik';
import React, { useEffect, useState } from 'react'
import CareLineOccupHealthFieldArea from '../../components/careLineOccupHealthField/CareLineOccupHealthFieldArea';
import ContainerWithSidebar from '../../components/UI/containerWithSidebar/ContainerWithSidebar';
import { GeneralContainer, TextLink } from '../../components/UI/generalLayout/Layout.styled';
import ScreenHeader from '../../components/UI/screenHeader/ScreenHeader';
import BackButton from '../../components/UI/buttons/BackButton';
import Colors from '../../constants/Colors';
import { initialObjectCareLineField } from '../../constants/Objects';
import * as yup from 'yup';
import * as occupHealthActions from '../../store/actions/occupationalHealth';
import { FormContainer } from '../../components/form/Form.styled';
import FormikTextField from '../../components/form/FormikTextField';
import useResponsiveInfo from '../../hooks/useResponsiveInfo';
import CareLineOccupHealthBottomButtonArea from '../../components/careLineOccupHealthField/CareLinOccupHealthBottomButtonArea';
import GenericModal from '../../components/genericModal/GenericModal';
import SuccessModal from '../../components/successModal/SuccessModal';
import { ReactComponent as CareLineModal } from '../../assets/imgs/careLineModal.svg'
import { useAppDispatch } from '../..';
import { useLocation, useNavigate } from 'react-router-dom';
import { Paths } from '../../enums/Paths';
import Category from '../../models/core/careLine/Category';
import { CareLineFields } from '../../enums/CareLineFields';
import { buildFieldList, getCategoriesOrProceduresForEdit, getExamsForEdit, getVitalSignsForEdit, isFieldlistEmpty, removeEmptyObjectsFromList, validateEmptyListItem, validateListHasAnyEmptyValue } from '../../helpers/CareLineHelperFunctions';
import Exam from '../../models/core/careLine/Exam';
import Procedure from '../../models/core/careLine/Procedure';
import VitalSign from '../../models/core/careLine/VitalSign';
import OccupationalHealth from '../../models/core/occupationalHealth/OccupationalHealth';
import { handleBackendErrors, isEmptyObject } from '../../helpers/HelperFunctions';
import { useSelector } from 'react-redux';
import Company from '../../models/core/company/Company';

// --------------------------------------------------------
// OCCUPATIONAL HEALTH REGISTER INTERFACE
// --------------------------------------------------------
/**
 * A component that renders a Screen.
 *
 * @param props Properties
 * @param props.route The route
 * @param props.navigation The navigator
 * @returns The component JSX.
 */
function OccupationalHealthRegister(): JSX.Element {
  // --------------------------------------------------------
  // OCCUPATIONAL HEALTH REGISTER PRIVATE DECLARATIONS
  // --------------------------------------------------------
  const validationSchema = yup.object().shape({
    nome: yup.string().min(3, 'Digite pelo menos 3 caracteres').required('Nome é um campo obrigatório!'),
  })

  const { state }: any = useLocation();

  const [isView] = useState(state != null && state.isView)
  const [isEdit] = useState(state != null)
  const [noFieldEditedOnEditState, setNoFieldEditedOnEditState] = useState(isEdit)
  const [showModalVoltar, setShowModalVoltar] = useState(false)
  const [showModalSucesso, setShowModalSucesso] = useState(false)
  const [categoriesOccupHealth, setCategories] = useState(isView ? [] : [initialObjectCareLineField]) 
  const [vitalSignsOccupHealth, setVitalSigns] = useState(isView ? [] : [initialObjectCareLineField]) 
  const [examsOccupHealth, setExams] = useState(isView ? [] : [initialObjectCareLineField]) 
  const [proceduresOccupHealth, setProcedures] = useState(isView ? [] : [initialObjectCareLineField]) 
  const [isConfirmationState, setIsConfirmationState] = useState(false)
  const [categoriaError, setCategoriaError] = useState('')
  const [sinaisVitaisError, setVitalSignsError] = useState('')
  const [examesError, setExamesError] = useState('')
  const [procedimentosError, setProcedimentosError] = useState('')
  const [generalErrors, setGeneralErrors] = useState<any[]>([])
  
  const { isMobile } = useResponsiveInfo()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const currentCompany: Company = useSelector((state: any)=>state.login.currentCompany)
  const allExams: Exam[] = useSelector((state: any) => state.careLine.allExams)
  const allProcedures: Procedure[] = useSelector((state: any) => state.careLine.allProcedures)
  const allCategories: Category[] = useSelector((state: any) => state.careLine.allCategories)
  const allVitalSigns: VitalSign[] = useSelector((state: any) => state.careLine.allVitalSigns)

  const getInitialValue = ()=>{
    if(state !== null){
      const { occupHealth }: any = state

      return { nome: occupHealth.nome, id: occupHealth.id ?? '' }
    }else{
      return { nome: '', id: '' }
    }
  }

  const resetErrors = () => {
    setCategoriaError('')
    setExamesError('')
    setProcedimentosError('')
    setGeneralErrors([])
  }


  const navigateToOccupationalHealthList = ()=>{
    return navigate(Paths.OccupationalHealthList)
  }

  const register =  async (values: any)=>{
    try{
      await dispatch(occupHealthActions.registerOccupationalHealth(currentCompany.id, values))
      setShowModalSucesso(true)
    }catch(error: any){
      setIsConfirmationState(false)
      handleBackendErrors(error, setGeneralErrors)
    }
  }

  const update = async (values: any)=>{
    try{
      await dispatch(occupHealthActions.editOccupationalHealth(currentCompany.id, values, values.id))
      setShowModalSucesso(true)
    }catch(error: any){
      setIsConfirmationState(false)
      handleBackendErrors(error, setGeneralErrors)
    }
  }

  const validateCareLineOccupHealthListItems = ()=>{
    return !isFieldlistEmpty(categoriesOccupHealth) 
      && (!isFieldlistEmpty(examsOccupHealth) || !isFieldlistEmpty(proceduresOccupHealth) || !isFieldlistEmpty(vitalSignsOccupHealth))
  }

  const onRegister = async (values: any)=>{
    try{
      await validationSchema.validate(values)

      if(!validateCareLineOccupHealthListItems()){
        setGeneralErrors([{message: 'Saúde Ocupacional deverá conter uma Categoria e pelo menos uma das informações: Sinal Vital, Exame ou Procedimento'}])
        return;
      }

      const exames: Exam[] = buildFieldList(examsOccupHealth, CareLineFields.Exame)
      const categorias: Category[] = buildFieldList(categoriesOccupHealth, CareLineFields.Categoria)
      const procedimentos: Procedure[] = buildFieldList(proceduresOccupHealth, CareLineFields.Procedimento)
      const sinaisVitais: VitalSign[] = buildFieldList(vitalSignsOccupHealth, CareLineFields.SinalVital)
      
      /* Ajustar validação de erro */
      let categoriaError = false;
      let examesError = false;
      let procedimentosError = false;
      let sinaisVitaisError = false;

      if(!validateEmptyListItem(categorias) && validateListHasAnyEmptyValue(categoriesOccupHealth, true, false)){
        categoriaError = true;
        setCategoriaError('Preencha todas as informações das Categorias')
      }else{
        categoriaError = false;
        setCategoriaError('')
      }

      if(!validateEmptyListItem(exames) && validateListHasAnyEmptyValue(examsOccupHealth, true, true)){
        examesError = true
        setExamesError('Preencha todas as informações dos Exames')
      }else{
        examesError = false
        setExamesError('')
      }

      if(!validateEmptyListItem(procedimentos) && validateListHasAnyEmptyValue(proceduresOccupHealth, true, false)){
        procedimentosError = true
        setProcedimentosError('Preencha todas as informações dos Procedimentos')
      }else{
        procedimentosError = false
        setProcedimentosError('')
      }

      if(!validateEmptyListItem(sinaisVitais) && validateListHasAnyEmptyValue(vitalSignsOccupHealth, false, false)){
        sinaisVitaisError = true;
        setVitalSignsError('Preencha todas as informações dos Sinais Vitais')
      }else{
        sinaisVitaisError = false;
        setVitalSignsError('')
      }

      if(procedimentosError || examesError || categoriaError || sinaisVitaisError){
        return;
      }

      const occupationalHealth = new OccupationalHealth().fromJSON({
        id: values.id,
        nome: values.nome,
        empresaId: currentCompany.id,
        categorias: categorias, 
        exames: exames,
        procedimentos: procedimentos, 
        sinaisVitais: sinaisVitais
      })

      resetErrors()
      setIsConfirmationState(true)
      isConfirmationState? (isEdit? await update(occupationalHealth) : await register(occupationalHealth)) 
      : setIsConfirmationState(true); window.scrollTo({top: 0, left: 0, behavior: 'smooth'});

    }catch(error: any){
      setIsConfirmationState(false)
      handleBackendErrors(error, setGeneralErrors)
    } 
  }

  const onVoltar = (values: any) => {
    const examesFinal: any[] = examsOccupHealth.filter(e=>{
      return e.nome !== ''
    })

    const categoriasFinal: any[] = categoriesOccupHealth.filter(c=>{
      return c.nome !== ''
    })

    const procedimentosFinal: any[] = proceduresOccupHealth.filter(p=>{
      return p.nome !== ''
    })

    const sinaisVitaisFinal: any[] = vitalSignsOccupHealth.filter(v=>{
      return v.nome !== ''
    })

    if ((isEmptyObject(values) 
        && categoriasFinal.length === 0 
        && procedimentosFinal.length === 0 
        && examesFinal.length === 0 
        && sinaisVitaisFinal.length === 0)
        || isView || noFieldEditedOnEditState) {
      navigateToOccupationalHealthList()
    } else {
      setShowModalVoltar(true)
    }
  }

  useEffect(() => {
    if (state !== null) {
      const { occupHealth }: any = state;
      occupHealth.exames.length > 0 && setExams(getExamsForEdit(occupHealth, allExams))
      occupHealth.categorias.length > 0 && setCategories(getCategoriesOrProceduresForEdit(true, occupHealth, allCategories, allProcedures))
      occupHealth.procedimentos.length > 0 && setProcedures(getCategoriesOrProceduresForEdit(false, occupHealth, allCategories, allProcedures))
      occupHealth.sinaisVitais.length > 0 && setVitalSigns(getVitalSignsForEdit(occupHealth, allVitalSigns))
    }
  }, [allCategories, allExams, allProcedures, allVitalSigns])

  const getPageTitle = ()=>{
    if(isView){
      return 'Visualizar Saúde Ocupacional'
    }else if(isEdit){
      return 'Editar Saúde Ocupacional'
    }else{
      return 'Cadastro de Saúde Ocupacional'
    }
  }

  return (
    <ContainerWithSidebar>
        <GeneralContainer>
            <Formik
            initialValues={getInitialValue()}
            onSubmit={() => { }}
            validationSchema={validationSchema}
            validateOnChange={false}
            validateOnBlur={false}
            >
                {(formik) => {
                    const { values, errors } = formik;

                    if(isEdit){
                      if(values.nome !== getInitialValue().nome){
                        setNoFieldEditedOnEditState(false);
                      }
                    }
                    
                    return (
                      <Form>
                          <BackButton text="Voltar para listagem de saúde ocupacional" onClick={() => onVoltar(values)} />
                          <FormContainer isConfirmationState={isConfirmationState}>
                                <ScreenHeader
                                    hasRightButton={false}
                                    screenTitle={getPageTitle()}
                                    rightButtonText={''}
                                    onRightButtonClick={() => {}}
                                    errors={generalErrors}
                                    onCloseError={()=>{ setGeneralErrors([]) }}
                                />
                                <FormikTextField
                                    variant="standard"
                                    name="nome"
                                    label="Nome da saúde ocupacional"
                                    disabled={isConfirmationState || isView}
                                    error={errors.nome !== undefined}
                                    helperText={errors.nome !== undefined ? errors.nome : null}
                                    inputProps={{ maxLength: 100 }}
                                    sx={{ width: isMobile ? '100%' : '49%', marginBottom: 6 }}
                                />                                
                                <CareLineOccupHealthFieldArea 
                                    categoryError={categoriaError}
                                    examsError={examesError}
                                    proceduresError={procedimentosError}
                                    vitalSignsError={sinaisVitaisError}
                                    setNoFieldEditedOnEditState={setNoFieldEditedOnEditState}
                                    categories={isConfirmationState? removeEmptyObjectsFromList(categoriesOccupHealth) : categoriesOccupHealth} 
                                    setCategories={setCategories} 
                                    vitalSigns={isConfirmationState? removeEmptyObjectsFromList(vitalSignsOccupHealth) : vitalSignsOccupHealth} 
                                    setVitalSigns={setVitalSigns} 
                                    exams={isConfirmationState? removeEmptyObjectsFromList(examsOccupHealth) : examsOccupHealth} 
                                    setExams={setExams} 
                                    procedures={isConfirmationState? removeEmptyObjectsFromList(proceduresOccupHealth) : proceduresOccupHealth} 
                                    setProcedures={setProcedures} 
                                    isConfirmationState={isConfirmationState} 
                                    isView={isView}
                                />
                              </FormContainer>
                                
                              <CareLineOccupHealthBottomButtonArea 
                                    isConfirmationState={isConfirmationState} 
                                    isView={isView} 
                                    isFinalizarDisabled={noFieldEditedOnEditState}
                                    onEditClick={()=>{ setIsConfirmationState(false) }} 
                                    onCancelClick={()=>{ onVoltar(values) }} 
                                    onFinalizarClick={()=>{ onRegister(values) }} 
                                    onVoltarClick={navigateToOccupationalHealthList} 
                              />
                        </Form>
                    )
                }}
            </Formik>
        </GeneralContainer>
        <SuccessModal
          Icon={() => { return <CareLineModal /> }}
          modalMsg="Cadastro da saúde ocupacional concluído com sucesso!"
          visible={showModalSucesso}
          onVoltar={navigateToOccupationalHealthList}
        />
        
        <GenericModal
          visible={showModalVoltar}
          onCancel={() => { setShowModalVoltar(false) }}
          onConfirm={navigateToOccupationalHealthList}
          modalTitle={'Tem certeza que deseja sair do cadastro de saúdes ocupacionais?'}
          modalSubtitle={'Os dados preenchidos não serão salvos'}
          cancelButtonMsg={'Confirmar'}
          confirmButtonMsg={'Voltar'}
        />
    </ContainerWithSidebar>
    );
}

export default OccupationalHealthRegister;
